import { supabase } from '../../lib/supabase';

/*
  Reading the item catalog without downloading it.

  **The screen used to fetch the whole thing before it showed anything**: every
  row, all 55 columns, in one trip after another, each waiting for the last, and
  then it sorted and filtered in the browser. That is why it was slow, and it is
  not the database, whose indexes are fine. It was 5,000 rows in 5 trips when
  this was written on 6 September 2026, and the build order said 34,340 in 35
  because it was written when MedEx still had a catalog.

  Three things fix it and all three are here: **ask for a page at a time, ask for
  the columns the list actually shows, and let the database do the searching.**

  Count entry has never been affected: it searches with a limit of 15.
*/

/* The seven the list draws, out of fifty-five. The whole row is read only when
   somebody opens one. */
export const LIST_COLUMNS =
  'id, item_number, description, vendor, source, match_status, match_locked';

export const PAGE_SIZE = 50;

/*
  **A search box is a hole straight into the query unless it is escaped**, and
  there are two separate escapings here, in this order. Get either wrong and a
  search either matches the wrong rows silently or matches nothing at all.

  1. **The database's own wildcards.** `%` matches anything and `_` matches any
     single character. Item numbers in this catalog contain underscores:
     `_014511` is a real one. Searching for it unescaped matches every item with
     any character in that position, which is not nothing, it is quietly wrong.
  2. **The way several conditions are sent as one.** Searching three columns at
     once sends them in one string separated by commas, so a comma typed into
     the box would split it into conditions that mean something else. A value in
     quotes is taken whole, and inside quotes a quote mark and a backslash each
     need a backslash of their own.
*/
export function likePattern(term) {
  return '%' + String(term ?? '').replace(/[\\%_]/g, m => '\\' + m) + '%';
}

export function searchClause(columns, term) {
  const quoted = '"' + likePattern(term).replace(/[\\"]/g, m => '\\' + m) + '"';
  return columns.map(c => c + '.ilike.' + quoted).join(',');
}

const SEARCH_COLUMNS = ['item_number', 'description', 'vendor'];

/*
  **The item a scanned barcode belongs to**, asked of the database, 18
  September 2026 (the audit after the rebrand, issue 13). The phone's Scan used
  to look the code up in a list called `items` that this page stopped keeping
  when the catalog began reading a page at a time, so it stopped dead after
  reading a code and said nothing. Exact and case-blind, across all six slots,
  with the same escaping as a typed search so a code is taken whole.

  **The database had TWO barcode columns until 20 September 2026 and the app
  named six**, and a question that names a column that does not exist is
  refused outright. Proved on the live database on 18 September: the count
  sheet's scanner asked about `barcode_3` and got back "column
  items.barcode_3 does not exist", so scanning a barcode on a count sheet had
  never found an item.

  **It is six now**, the owner's answer to the audit's question 25: *"I need it
  to hold potentially 6 barcodes."* The four missing columns were added
  (`20260920120000_six_barcodes_per_item.sql`) and everything that reads a
  barcode reads this one list, so the two can never drift apart again.
*/
export const BARCODE_FIELDS = ['barcode_1', 'barcode_2', 'barcode_3', 'barcode_4', 'barcode_5', 'barcode_6'];

export function barcodeClause(code) {
  const exact = String(code ?? '').trim().replace(/[\\%_]/g, m => '\\' + m);
  const quoted = '"' + exact.replace(/[\\"]/g, m => '\\' + m) + '"';
  return BARCODE_FIELDS.map(f => f + '.ilike.' + quoted).join(',');
}

export async function findByBarcode(code) {
  if (!String(code ?? '').trim()) return { item: null, error: null };
  const { data, error } = await supabase.from('items').select(LIST_COLUMNS)
    .or(barcodeClause(code)).order('item_number').limit(1);
  return { item: (data || [])[0] || null, error };
}

/*
  **Saving an item's barcodes**, the owner's answers of 18 September 2026
  (question 18): the X beside a saved barcode removes it, and one barcode
  belongs to one item.

  Until then the X only cleared what had just been typed, and saving never
  touched a slot left empty, so a saved barcode could not be taken off by
  anybody; and a barcode already on another item saved without a word, so a
  scan found whichever of the two came first. **Sixteen barcodes in the test
  catalog are on two or three items already**, all from ClaimSoft's own data,
  and those are left as they are for the owner to decide about.

  **One item within one catalog.** An item and its twin in the other catalog are
  the same product, so the check looks only among the item's own catalog.

  `slots` is every box as it now reads, { barcode_1: '...', ... }. Each is
  compared with what the item has saved: a box that changed to a new code is
  written (after the check), a box that was emptied removes that barcode, and a
  box left as it was is not touched, so an item that already shares a barcode
  from ClaimSoft can still be saved. Returns { ok, error, clash, nothing },
  where `clash` is the item that already carries a code being added. The write
  is read back, because a save no rule allows changes nothing and answers
  "fine".
*/
export async function saveBarcodes(item, slots) {
  const update = {};
  for (const [slot, text] of Object.entries(slots || {})) {
    const now = String(text ?? '').trim();
    const before = String(item?.[slot] ?? '').trim();
    if (now === before) continue;
    update[slot] = now || null;
  }
  if (!Object.keys(update).length) return { ok: false, error: null, clash: null, nothing: true };

  for (const code of Object.values(update).filter(Boolean)) {
    const { data, error } = await supabase.from('items').select('id, item_number, description')
      .or(barcodeClause(code)).eq('source', item.source).neq('id', item.id).limit(1);
    if (error) return { ok: false, error, clash: null };
    if (data && data.length) return { ok: false, error: null, clash: { code, ...data[0] } };
  }

  const { data, error } = await supabase.from('items').update(update).eq('id', item.id).select('id');
  if (error) return { ok: false, error, clash: null };
  return { ok: !!(data && data.length), error: null, clash: null };
}

/*
  **Undoing a match set by hand** (question 18, 18 September 2026). Setting one
  by hand locks it so an import leaves it alone, and there was no way back.
  This takes the lock off and runs the same matching pass an import runs, so
  OnHand decides the item again straight away rather than at the next import.
*/
export async function releaseMatch(id, runPass) {
  const { data, error } = await supabase.from('items').update({ match_locked: false })
    .eq('id', id).select('id, match_locked').maybeSingle();
  if (error) return { ok: false, error };
  if (!data || data.match_locked) return { ok: false, error: null };
  let note = '';
  try { note = await runPass(supabase); } catch (e) { note = ' (matching did not run: ' + e.message + ')'; }
  return { ok: true, error: null, note };
}

/*
  One page of the catalog, and how many rows there are altogether.

  `total` is the number the filters matched, not the number on the screen, which
  is what the bar says and what "Load more" counts down from.
*/
export async function loadPage({ search = '', vendor = '', source = '', status = '', from = 0, size = PAGE_SIZE } = {}) {
  let q = supabase.from('items').select(LIST_COLUMNS, { count: 'exact' });
  if (vendor) q = q.eq('vendor', vendor);
  if (source) q = q.eq('source', source);
  if (status) q = q.eq('match_status', status);
  if (search.trim()) q = q.or(searchClause(SEARCH_COLUMNS, search.trim()));
  const { data, error, count } = await q.order('item_number').range(from, from + size - 1);
  return { rows: data || [], total: count ?? 0, error };
}

/* The whole row, once. Fifty-five columns are worth reading for one item and
   are not worth reading for five thousand. */
export async function loadItem(id) {
  const { data, error } = await supabase.from('items').select('*').eq('id', id).maybeSingle();
  return { item: data || null, error };
}

/*
  The Account Edge twin of a ClaimSoft item.

  **They are two rows in one table**, told apart by `source`, and the link is the
  item number written on the ClaimSoft row. So the twin is looked up by number
  rather than by an id, which is why it can be missing even when the number is
  set: a re-import can take the Account Edge row away and leave the number
  pointing at nothing.
*/
export async function loadTwin(item) {
  if (!item || item.source !== 'claimsoft' || !item.edge_item_number) {
    return { twin: null, error: null };
  }
  const { data, error } = await supabase.from('items').select('*')
    .eq('source', 'edge').eq('item_number', item.edge_item_number).maybeSingle();
  return { twin: data || null, error };
}

/* Candidates for a person choosing the twin by hand: Account Edge rows whose
   number or vendor part number looks like this item's number. */
export async function findEdgeCandidates(term, limit = 20) {
  const q = (term || '').trim();
  if (!q) return { rows: [], error: null };
  const { data, error } = await supabase.from('items')
    .select('id, item_number, description, vendor, vendor_part_number')
    .eq('source', 'edge')
    .or(searchClause(['item_number', 'vendor_part_number', 'description'], q))
    .order('item_number')
    .limit(limit);
  return { rows: data || [], error };
}

export async function loadVendors() {
  const { data, error } = await supabase.rpc('catalog_vendors');
  return { vendors: data || [], error };
}

/*
  How many items are in each state, for the strip above the rows and for the
  screen that works through the unverified ones.

  **Counted by the database, one request each, with no rows sent back.** Asking
  for the rows in order to count them is the fault this whole file exists to
  undo.
*/
export async function loadCounts() {
  const one = async where => {
    let q = supabase.from('items').select('id', { count: 'exact', head: true });
    for (const [col, val] of Object.entries(where)) q = q.eq(col, val);
    const { count, error } = await q;
    return error ? 0 : (count || 0);
  };
  const [claimsoft, edge, confirmed, unverified, rejected, noMatch, locked] = await Promise.all([
    one({ source: 'claimsoft' }),
    one({ source: 'edge' }),
    one({ source: 'claimsoft', match_status: 'confirmed' }),
    one({ source: 'claimsoft', match_status: 'unverified' }),
    one({ source: 'claimsoft', match_status: 'rejected' }),
    one({ source: 'claimsoft', match_status: 'no_match' }),
    one({ source: 'claimsoft', match_locked: true }),
  ]);
  return { claimsoft, edge, confirmed, unverified, rejected, noMatch, locked };
}

/*
  The four words for where a ClaimSoft item stands against Account Edge, in
  plain English rather than in the words the database stores.

  **`no_match` is not a failure and neither is `unverified`**, which is the whole
  point of the matching rules: a match is rejected only on positive disagreement,
  so "we could not tell" and "there is nothing to tell" are different answers and
  both are honest.
*/
export const MATCH_WORDS = {
  confirmed:  { label: 'Matched',      tone: 'app',    says: 'The vendors agree, so this is the same product in both catalogs.' },
  unverified: { label: 'Not sure',     tone: 'gold',   says: 'The numbers line up but the vendor could not be checked, so nobody has said yes or no.' },
  rejected:   { label: 'Not a match',  tone: 'ns',     says: 'The numbers line up and the vendors are provably different, so it was turned down.' },
  no_match:   { label: 'No twin',      tone: 'blue',   says: 'Nothing in Account Edge carries this number.' },
};

export const METHOD_WORDS = {
  direct:             'by item number',
  via_vendor_number:  'by the vendor part number',
  /* The third value, added 6 September 2026 when a person could set a link for
     the first time. `MATCHING_SPEC.md` named two and now names three. */
  manual:             'by hand',
};

export function matchWords(item) {
  return MATCH_WORDS[item?.match_status] || null;
}

/*
  Linking an item to a twin by hand, and locking it so a re-import leaves it
  alone.

  **`match_locked` has been a column nothing writes.** The matching pass has read
  it since it was built, and skips a locked row exactly as `MATCHING_SPEC.md`
  says, so a manual link would survive a re-import. No screen has ever set one,
  so every row in the catalog says false and there are no manual links at all.

  **The write is read back**, because a save that no rule allows changes no rows,
  raises nothing, and answers "fine".
*/
export async function setMatch(id, { edgeItemNumber, status, locked }) {
  const patch = {
    edge_item_number: edgeItemNumber ?? null,
    match_status: status,
    match_method: edgeItemNumber ? 'manual' : null,
    match_locked: !!locked,
  };
  const { data, error } = await supabase.from('items').update(patch).eq('id', id)
    .select('id, edge_item_number, match_status, match_locked').maybeSingle();
  if (error) return { ok: false, error, row: null };
  const ok = !!data
    && data.match_status === patch.match_status
    && !!data.match_locked === patch.match_locked
    && (data.edge_item_number || null) === patch.edge_item_number;
  return { ok, error: null, row: data || null };
}
